import { useState, useEffect, useContext } from "react";
import { Button, Form, Modal, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const EMPTY_FORM = {
  name: "",
  size: "",
  qty: "",
  qty_type: "",
  description: "",
};

export default function AddEditRawMaterials({
  show,
  onHide,
  onSuccess,
  material,
}) {
  const { userData } = useContext(AuthContext);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!material?.material_id;

  useEffect(() => {
    if (show) {
      setError("");
      setLoading(false);
      const newFormData = material
        ? {
            name: material.name || "",
            size: material.size || "",
            qty: material.qty || "",
            qty_type: material.qty_type || "",
            description: material.description || "",
          }
        : EMPTY_FORM;
      setFormData(newFormData);
    }
  }, [show, material]);

  const handleChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        company_code: userData?.company_code,
        material_name: formData.name,
        material_size: formData.size,
        material_qty: formData.qty,
        qty_type: formData.qty_type,
        description: formData.description,
      };

      if (isEdit) {
        const response = await axios.put(
          `${BASE_URL}/api/admin/raw-materials/update/${material.material_id}`,
          payload,
        );
      } else {
        await axios.post(`${BASE_URL}/api/admin/raw-materials/add`, payload);
      }

      onSuccess(isEdit ? "updated" : "added");
      onHide();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to save. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Raw Material" : "Add Raw Material"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              Material Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Enter material name"
              value={formData.name}
              onChange={handleChange("name")}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Size</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter size"
              value={formData.size}
              onChange={handleChange("size")}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Quantity (If existing)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter quantity"
              value={formData.qty}
              onChange={handleChange("qty")}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Quantity Type <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              required
              value={formData.qty_type}
              onChange={handleChange("qty_type")}
              disabled={loading}
            >
              <option value="">Select quantity type</option>
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="m">m</option>
              <option value="cm">cm</option>
              <option value="pcs">pcs</option>
              <option value="liters">liters</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange("description")}
              disabled={loading}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="primary"
              className="flex-grow-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : isEdit ? (
                "Update Material"
              ) : (
                "Add Material"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
